import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Card,
    Row,
    Col,
    Typography,
    Space,
    Progress,
    Empty,
    Statistic,
    Tag,
    List,
    Timeline,
    Button,
    message
} from 'antd';
import {
    ClockCircleOutlined,
    CheckCircleOutlined,
    BarChartOutlined,
    TrophyOutlined,
    TeamOutlined,
    DownloadOutlined
} from '@ant-design/icons';
import { ISurveyResponse, ISurveyResponseSummary, ISurvey } from '../../services/nestjs-base-api/types';
import { useExportParticipantListMutation, useExportAdminParticipantListMutation } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { Utils } from '../../utils/utils';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface ResponseAnalyticsProps {
    responses: ISurveyResponse[];
    summary?: ISurveyResponseSummary;
    loading: boolean;
    survey: ISurvey;
}

const ResponseAnalytics: React.FC<ResponseAnalyticsProps> = ({ 
    responses, 
    summary, 
    loading, 
    survey 
}) => {
    const intl = useIntl();
    const { user } = useAuth();
    
    const [exportParticipants, { isLoading: isExportingParticipants }] = useExportParticipantListMutation();
    const [exportAdminParticipants, { isLoading: isExportingAdminParticipants }] = useExportAdminParticipantListMutation();

    const handleExportParticipants = async () => {
        try {
            // Pick the endpoint by role, not by URL. /admin/surveys/... is
            // guarded by PlatformAdminGuard, so an org user calling it gets a
            // 403. This used to sniff the path for '/admin/', which broke once
            // the Marketer View shell gained its own admin section: an org user
            // at /dashboard/:brandHash/admin/surveys/:hash/results matches that
            // test and was sent to the platform-admin endpoint. Role is what
            // the guard actually checks, so key off the same thing.
            const isAdminView = Utils.userIsPlatformAdmin(user);
            const exportMutation = isAdminView ? exportAdminParticipants : exportParticipants;
            
            const result = await exportMutation({
                jwtToken: user!.jwtToken,
                surveyId: survey.id
            }).unwrap();

            // Create download link
            const url = window.URL.createObjectURL(result.blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = result.filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            message.success(intl.formatMessage({ id: 'survey.results.exportSuccess' }));
        } catch (error) {
            message.error(intl.formatMessage({ id: 'survey.results.exportError' }));
        }
    };
    if (loading) {
        return <div>Loading...</div>;
    }

    if (!summary || responses.length === 0) {
        return (
            <Empty
                description={<FormattedMessage id="survey.results.analytics.noData" />}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
        );
    }

    const getQuestionAnalytics = () => {
        const questionStats = new Map<number, {
            questionText: string;
            category: string;
            totalResponses: number;
            responses: { value: string | number; count: number }[];
        }>();

        responses.forEach(response => {
            response.questionResponses.forEach(qResponse => {
                const existing = questionStats.get(qResponse.questionId) || {
                    questionText: qResponse.question.text,
                    category: qResponse.question.category,
                    totalResponses: 0,
                    responses: []
                };

                existing.totalResponses += 1;
                
                // Get the response value
                const responseValue = qResponse.numericValue !== null ? 
                    qResponse.numericValue : 
                    (qResponse.textValue || 'No response');
                
                const valueStr = responseValue.toString();
                const existingResponse = existing.responses.find(r => r.value.toString() === valueStr);
                if (existingResponse) {
                    existingResponse.count += 1;
                } else {
                    existing.responses.push({ value: responseValue, count: 1 });
                }

                questionStats.set(qResponse.questionId, existing);
            });
        });

        const result = Array.from(questionStats.entries()).map(([id, stats]) => ({
            id,
            ...stats,
            responseRate: 100 // All answered questions have 100% response rate in this data structure
        }));

        return result.sort((a, b) => b.totalResponses - a.totalResponses);
    };

    const getResponseTimeline = () => {
        const timeline = responses
            .filter(r => r.questionResponses.length > 0) // Filter responses that have actual question responses
            .sort((a, b) => dayjs(a.submittedAt).unix() - dayjs(b.submittedAt).unix())
            .slice(-10); // Last 10 completed responses

        return timeline;
    };

    const getTopParticipants = () => {
        const participantStats = responses.reduce((acc, response) => {
            const key = response.participantIdentifier || response.participantEmail || 'Anonymous';
            if (!acc[key]) {
                acc[key] = {
                    identifier: key,
                    responses: 0,
                    totalQuestions: 0,
                    completedSurveys: 0
                };
            }
            
            acc[key].responses += 1;
            acc[key].totalQuestions += response.questionResponses.length;
            if (response.questionResponses.length > 0) {
                acc[key].completedSurveys += 1;
            }
            
            return acc;
        }, {} as Record<string, any>);

        return Object.values(participantStats)
            .sort((a: any, b: any) => b.totalQuestions - a.totalQuestions)
            .slice(0, 5);
    };

    const questionAnalytics = getQuestionAnalytics();
    const recentCompletions = getResponseTimeline();
    const topParticipants = getTopParticipants();

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0 }}>
                    <FormattedMessage id="survey.results.analytics.overview" />
                </Title>
                <Button
                    type="primary"
                    loading={isExportingParticipants || isExportingAdminParticipants}
                    onClick={handleExportParticipants}
                    disabled={!summary || summary.uniqueParticipants === 0}
                >
                    <DownloadOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage id="survey.results.exportParticipants" />
                </Button>
            </div>
            
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={<FormattedMessage id="survey.results.analytics.totalParticipants" />}
                            value={summary.uniqueParticipants}
                            prefix={<TeamOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={<FormattedMessage id="survey.results.analytics.avgQuestions" />}
                            value={responses.length > 0 ? 
                                (responses.reduce((sum, r) => sum + r.questionResponses.length, 0) / responses.length).toFixed(1) 
                                : 0
                            }
                            prefix={<BarChartOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title={<FormattedMessage id="survey.results.analytics.avgResponseTime" />}
                            value={Math.round(summary.averageCompletionTime / 60)}
                            suffix="min"
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <Progress
                                type="circle"
                                percent={Math.round(summary.completionRate)}
                                size={80}
                                format={percent => `${percent}%`}
                            />
                            <div style={{ marginTop: '8px' }}>
                                <Text type="secondary">
                                    <FormattedMessage id="survey.results.analytics.completionRate" />
                                </Text>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <Space>
                                <BarChartOutlined />
                                <FormattedMessage id="survey.results.analytics.questionPerformance" />
                            </Space>
                        }
                    >
                        <List
                            dataSource={questionAnalytics.slice(0, 5)}
                            renderItem={(item) => (
                                <List.Item>
                                    <div style={{ width: '100%' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <Text strong>{item.questionText}</Text>
                                        </div>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Space>
                                                    <Text type="secondary">
                                                        <FormattedMessage id="survey.results.analytics.responses" />:
                                                    </Text>
                                                    <Tag color="blue">{item.totalResponses}</Tag>
                                                </Space>
                                            </Col>
                                            <Col span={12}>
                                                <Space>
                                                    <Text type="secondary">
                                                        <FormattedMessage id="survey.results.analytics.responseRate" />:
                                                    </Text>
                                                    <Tag color={item.responseRate > 80 ? 'green' : 'orange'}>
                                                        {item.responseRate.toFixed(1)}%
                                                    </Tag>
                                                </Space>
                                            </Col>
                                        </Row>
                                        <div style={{ marginTop: '8px' }}>
                                            <Progress
                                                percent={item.responseRate}
                                                size="small"
                                                showInfo={false}
                                                strokeColor={item.responseRate > 80 ? '#52c41a' : '#faad14'}
                                            />
                                        </div>
                                    </div>
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <Space>
                                <TrophyOutlined />
                                <FormattedMessage id="survey.results.analytics.topParticipants" />
                            </Space>
                        }
                    >
                        <List
                            dataSource={topParticipants}
                            renderItem={(participant, index) => (
                                <List.Item>
                                    <div style={{ width: '100%' }}>
                                        <Row justify="space-between" align="middle">
                                            <Col>
                                                <Space>
                                                    <Tag color="blue">#{index + 1}</Tag>
                                                    <Text strong>
                                                        {participant.identifier === 'Anonymous' ? 
                                                            <FormattedMessage id="survey.results.analytics.anonymous" /> :
                                                            participant.identifier
                                                        }
                                                    </Text>
                                                </Space>
                                            </Col>
                                            <Col>
                                                <Space>
                                                    <Tag color="green">
                                                        {participant.totalQuestions} <FormattedMessage id="survey.results.analytics.questions" />
                                                    </Tag>
                                                    <Tag color="purple">
                                                        {participant.completedSurveys} <FormattedMessage id="survey.results.analytics.completed" />
                                                    </Tag>
                                                </Space>
                                            </Col>
                                        </Row>
                                    </div>
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>
            </Row>

            <Card
                title={
                    <Space>
                        <CheckCircleOutlined />
                        <FormattedMessage id="survey.results.analytics.recentActivity" />
                    </Space>
                }
            >
                {recentCompletions.length === 0 ? (
                    <Empty
                        description={<FormattedMessage id="survey.results.analytics.noRecentActivity" />}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                ) : (
                    <Timeline
                        items={recentCompletions.map(response => ({
                            color: 'green',
                            dot: <CheckCircleOutlined />,
                            children: (
                                <Space direction="vertical" size="small">
                                    <Text strong>
                                        {response.participantIdentifier || response.participantEmail || 
                                            <FormattedMessage id="survey.results.analytics.anonymous" />
                                        }
                                    </Text>
                                    <Text type="secondary">
                                        <FormattedMessage
                                            id="survey.results.analytics.completedSurvey"
                                            values={{
                                                questions: response.questionResponses.length,
                                                time: dayjs(response.submittedAt).format('MMM D, h:mm A')
                                            }}
                                        />
                                    </Text>
                                </Space>
                            ),
                        }))}
                    />
                )}
            </Card>
        </Space>
    );
};

export default ResponseAnalytics;